import { useState } from 'react'
import { useI18n } from '../i18n'
import { api } from '../lib/api'
import { myName, setMyName, shareMove } from '../lib/dialogs'
import { thumb } from '../lib/img'
import { renderOgCard } from '../lib/ogcard'
import { renderShareCard, shareOrDownload } from '../lib/sharecard'
import { useSession } from '../store/session'

type Props = {
  img: string
  state: string
  before?: string | null
  deltaSharp?: number | null
  /** телеметрия рисунка, чтобы связать его со штрихами */
  sessionId?: number | null
}

/**
 * Главный виральный контур: рисунок уходит не картинкой, а вопросом + ссылкой
 * на комнату. Ответить можно только в продукте — и ответ тоже рисунок.
 */
export function ShareBox({ img, state, before, deltaSharp, sessionId }: Props) {
  const { t } = useI18n()
  const s = useSession()
  const [kind, setKind] = useState<'duo' | 'group'>('duo')
  const [qIdx, setQIdx] = useState(1)
  const [custom, setCustom] = useState('')
  const [truth, setTruth] = useState(state)
  const [name, setName] = useState(myName())
  const [link, setLink] = useState<{ id: string; url: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(false)

  const options = [...t.share.presets, t.share.custom]
  const question = qIdx === 2 ? custom.trim() || t.share.presets[0] : t.share.presets[qIdx]

  const create = async () => {
    if (busy) return
    setBusy(true)
    setErr(false)
    try {
      setMyName(name)
      const small = await thumb(img, 640, 0.72)
      const ogImg = await renderOgCard({ img, who: name.trim(), question })
      const res = await api.createDialog({
        kind,
        displayName: name.trim(),
        first: { img: small, ogImg, question, truth: truth.trim() || state || null, sessionId: sessionId ?? null },
      })
      setLink(res)
      await shareMove(t.dialogs.shareQuestion(name.trim(), question, res.url), res.url)
    } catch {
      setErr(true)
    } finally {
      setBusy(false)
    }
  }

  const openRoom = async () => {
    if (!link) return
    const d = await api.getDialog(link.id)
    s.set({ activeDialog: d, focusMsgId: null })
    s.go('room')
  }

  const card = async () => {
    const blob = await renderShareCard({ before, after: img, state, deltaSharp })
    await shareOrDownload(blob, 'LINIA', state)
  }

  return (
    <div className="card">
      <h3>{t.share.card}</h3>
      <div className="chips">
        {options.map((q, i) => (
          <button key={q} className={'chip' + (i === qIdx ? ' sel' : '')} onClick={() => setQIdx(i)}>{q}</button>
        ))}
      </div>
      {qIdx === 2 && (
        <input className="ans-input" placeholder={t.replyq.customPlaceholder} value={custom} onChange={(e) => setCustom(e.target.value)} />
      )}

      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>{t.dialogs.truthLabel} · {t.dialogs.truthHint}</p>
      <input className="ans-input" style={{ marginTop: 6 }} placeholder={t.dialogs.truthPlaceholder} value={truth} onChange={(e) => setTruth(e.target.value)} />

      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>{t.share.forWhom}</p>
      <div className="kinds">
        <button className={'kind' + (kind === 'duo' ? ' sel' : '')} onClick={() => setKind('duo')}>
          <b>{t.dialogs.kindDuo}</b><span>{t.dialogs.kindDuoHint}</span>
        </button>
        <button className={'kind' + (kind === 'group' ? ' sel' : '')} onClick={() => setKind('group')}>
          <b>{t.dialogs.kindGroup}</b><span>{t.dialogs.kindGroupHint}</span>
        </button>
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>{t.dialogs.nameTitle}</p>
      <input className="ans-input" style={{ marginTop: 6 }} placeholder={t.dialogs.namePlaceholder} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />

      <div className="btn-row" style={{ marginTop: 12 }}>
        <button className="btn btn-blue" style={{ padding: 13 }} disabled={busy || !name.trim()} onClick={create}>
          {t.share.create}
        </button>
      </div>
      {err && <div className="banner">{t.errors.threadFailed}</div>}
      {link && (
        <div className="linkline">
          {link.url}&nbsp;
          <b style={{ color: 'var(--blue)', cursor: 'pointer' }} onClick={openRoom}>{t.share.open}</b>
        </div>
      )}
      <div className="sharecard-actions">
        <button className="tool-btn" onClick={card}>{t.share.shareCard}</button>
      </div>
    </div>
  )
}
